import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { HandIcon } from 'lucide-react'
import { Avatar } from '@repo/ui'
import {
  checkStageMove,
  isCommitted,
  isWonWithoutPaying,
  SALES_STAGES,
  usdValue,
  type SalesBoardLane,
  type SalesStage,
  type StageMoveCheck,
} from '@repo/types'

import { KanbanBoard, KanbanBoardSkeleton, type KanbanVerdict } from 'components/display/KanbanBoard'
import type { SalesDealCard } from 'services'

import { ToneChip } from '../InternalAnalyticsPage/components/shared'
import { DealMarks } from './AutoWonMarks'
import { AnnualValueFigure, TagMarginFigure } from './DealValueFigures'
import { basisLabel, daysLabel, dealTitle, LANE_LABEL, LANE_TERM, moneyShort, planLabel, sourceGroupLabel, sourceSummary, STAGE_LABEL, valueLabel } from './dealsModel'

/**
 * Column width floor. At 1280 with the sidebar open the board gets 893px (measured, VRG-691).
 * VRG-692: five stage lanes + the 52px collapsed Lost strip + five 12px gaps fit at 156:
 * 5 × 156 + 52 + 60 = 892. Narrower than that (or with Lost expanded), the board scrolls
 * sideways INSIDE its own container rather than squeezing (the page itself never does).
 */
const LANE_MIN = 156

export interface PendingMove {
  deal: SalesDealCard
  to: SalesStage
  check: StageMoveCheck
  /** The card came out of the Lost column: reopen it, then move (VRG-692). */
  reopen?: boolean
}

const DealCardView = ({ deal, onOpen }: { deal: SalesDealCard; onOpen: (d: SalesDealCard) => void }) => {
  const theme = useTheme()
  const basis = basisLabel(deal.value)
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(deal)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(deal)
      }}
      data-testid={`deal-card-${deal.id}`}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: 'background.paper',
        // The action-row pattern (§34): a click opens the company, so hover = brand border + ring.
        '&:hover, &:focus-visible': {
          borderColor: theme.palette.brand[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
          outline: 'none',
        },
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            {/* VRG-701 — "Company · Deal name": a company can have several cards now. */}
            <Typography variant="body3" sx={{ fontWeight: 500, minWidth: 0 }} noWrap title={dealTitle(deal)}>
              {dealTitle(deal)}
            </Typography>
            {/* VRG-700 — "auto" (Vera moved it to Won for a paying subscription) and the warning
                on a Won deal with nothing paying. Shared with the list rows and the drawer. */}
            <DealMarks wonWithoutPaying={isWonWithoutPaying(deal)} autoWon={deal.auto_won} />
          </Stack>
          {deal.owner ? (
            <Tooltip title={`Owner: ${deal.owner.name}`}>
              <Box sx={{ flexShrink: 0 }}>
                <Avatar name={deal.owner.name} size={22} />
              </Box>
            </Tooltip>
          ) : null}
        </Stack>
        {/* Wraps (§74's sibling at card scale): the basis chip drops under a long figure rather than clipping. */}
        <Stack direction="row" alignItems="center" spacing={2} useFlexGap sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 1 }}>
          <Tooltip title={deal.value.detail ?? (deal.value.amount === null ? 'No calculator, form or manual figure yet' : '')}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} color={deal.value.amount === null ? 'text.secondary' : 'text.primary'}>
              {valueLabel(deal.value)}
            </Typography>
          </Tooltip>
          {basis && <ToneChip tone={deal.value.basis === 'manual' ? 'info' : 'neutral'} label={basis} glossaryId="deal-value-basis" />}
        </Stack>
        {/* VRG-736 — tag margin beside the ARR (never in it), and the annual value of both. */}
        {deal.tag_margin != null && (
          <Stack direction="row" alignItems="center" spacing={2} useFlexGap sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 1 }} data-testid="deal-card-tags">
            <Typography variant="body3" color="text.secondary">
              + tags
            </Typography>
            <TagMarginFigure deal={deal} size="compact" />
            <Typography variant="body3" color="text.secondary">
              = annual
            </Typography>
            <AnnualValueFigure deal={deal} size="compact" />
          </Stack>
        )}
        {/* VRG-700 — a live, active $0 subscription with no paid invoice: its own chip, never Committed. */}
        {deal.pilot && !deal.forecast?.committed && (
          <Box>
            <ToneChip tone="caution" label="Pilot ($0)" glossaryId="sales-pilot-subscription" />
          </Box>
        )}
        {deal.forecast?.committed && (
          <Tooltip title={deal.forecast.actual_arr.detail ?? ''}>
            <Stack direction="row" alignItems="center" spacing={2} useFlexGap sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 1 }}>
              <ToneChip tone="positive" label="Committed" glossaryId="sales-committed-arr" />
              <Typography variant="body3" color="text.secondary" noWrap>
                {moneyShort(deal.forecast.actual_arr.amount)} ARR
              </Typography>
            </Stack>
          </Tooltip>
        )}
        <Tooltip title={sourceSummary(deal.source)}>
          <Stack direction="row" alignItems="center" spacing={2} useFlexGap sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 1 }}>
            <ToneChip
              tone={deal.source.group === 'expansion' ? 'info' : 'neutral'}
              label={sourceGroupLabel(deal.source.group)}
              glossaryId={deal.source.group === 'expansion' ? 'sales-expansion' : 'lead-source'}
            />
            {deal.plan && (
              <Typography variant="body3" color="text.secondary" noWrap>
                {planLabel(deal.plan)}
              </Typography>
            )}
            {deal.source.set_by === 'manual' && (
              <Typography variant="body3" color="text.secondary">
                set by hand
              </Typography>
            )}
          </Stack>
        </Tooltip>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="body3" color="text.secondary" noWrap>
            {deal.status === 'closed' ? `Lost at ${STAGE_LABEL[deal.stage]}` : `${daysLabel(deal.days_in_stage)} in stage`}
          </Typography>
          {deal.stage_set_by === 'manual' && (
            <Tooltip title="Moved here by hand; Vera has not seen the evidence for this stage yet.">
              <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }} aria-label="Set by hand">
                <HandIcon size={14} strokeWidth={1} />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}

/**
 * GTM → Sales → Pipeline: the deals board (VRG-691; columns VRG-692). A column per stage —
 * Lead → Access granted → Demo → Signed up (`account_created`) → Won — then LOST, collapsed by default to a
 * narrow strip with its count. Drag and drop (`@hello-pangea/dnd`):
 *   - between stages: moves the deal (a column the evidence rules out dims and refuses);
 *   - onto Lost: asks to confirm, with an optional reason, then closes the deal as lost;
 *   - out of Lost onto a stage: reopens it there (history records the reopen and the move).
 */
export const DealsBoard = ({
  deals,
  canManage,
  onOpen,
  onMove,
  onLost,
  showLost,
  onToggleLost,
}: {
  deals: readonly SalesDealCard[]
  canManage: boolean
  onOpen: (deal: SalesDealCard) => void
  /** Called for an allowed drop; the parent confirms past-evidence moves. */
  onMove: (move: PendingMove) => void
  /** A card dropped on Lost. */
  onLost: (deal: SalesDealCard) => void
  showLost: boolean
  onToggleLost: () => void
}) => {
  // VRG-734 — the lane/drag/strip mechanics live in the shared KanbanBoard now (extracted on its
  // second use, VERAfied episodes). Everything that is about DEALS stays here.
  const laneOf = (d: SalesDealCard): SalesBoardLane | null =>
    d.status === 'open' ? d.stage : d.closed_reason === 'lost' ? 'lost' : null

  const verdict = (deal: SalesDealCard, from: SalesBoardLane, lane: SalesBoardLane): KanbanVerdict => {
    if (lane === 'lost') return { kind: 'ok' }
    // Out of Lost back onto its own stage is a plain reopen.
    if (from === 'lost' && lane === deal.stage) return { kind: 'ok' }
    const c = checkStageMove(deal.stage, lane, deal.evidence)
    return c.kind === 'refused' ? { kind: 'refused', reason: c.reason } : { kind: 'ok' }
  }

  const drop = (deal: SalesDealCard, from: SalesBoardLane, lane: SalesBoardLane) => {
    if (lane === 'lost') return onLost(deal)
    const reopen = from === 'lost'
    const check: StageMoveCheck = reopen && lane === deal.stage ? { kind: 'ok' } : checkStageMove(deal.stage, lane, deal.evidence)
    if (check.kind === 'refused') return
    onMove({ deal, to: lane, check, reopen })
  }

  return (
    <KanbanBoard<SalesDealCard, SalesBoardLane>
      testId="deals"
      laneMin={LANE_MIN}
      lanes={SALES_STAGES.map((s) => ({ id: s, label: LANE_LABEL[s], term: LANE_TERM[s] }))}
      trailing={{ id: 'lost', label: LANE_LABEL.lost, term: LANE_TERM.lost, expanded: showLost, onToggle: onToggleLost, dropHint: 'Drop to mark lost' }}
      items={deals}
      getId={(d) => d.id}
      laneOf={laneOf}
      renderCard={(d) => <DealCardView deal={d} onOpen={onOpen} />}
      canDrag={canManage}
      verdict={verdict}
      onDrop={drop}
      laneSummary={(lane, cards) => {
        if (lane === 'lost') return `${cards.length} · not in the pipeline`
        // VRG-692 — a lane's sum is its OPEN PIPELINE: deals on an active subscription are
        // committed, so they are counted beside it, not in it. Σ stage lanes = the Open
        // pipeline tile. Lost deals are in no pipeline sum. The count sits here, not beside the
        // title: six columns at 1280 leave "Signed up" no room for it (§49).
        const committed = cards.filter((d) => isCommitted(d))
        const sum = cards.filter((d) => !isCommitted(d)).reduce((t, d) => t + (usdValue(d.value) ?? 0), 0)
        return `${cards.length} · ${moneyShort(sum)}${committed.length ? ` · ${committed.length} committed` : ''}`
      }}
      emptyLabel={(lane) => (lane === 'lost' ? 'No lost deals' : 'No deals')}
    />
  )
}

/** §19 — the board's footprint: five stage lanes of card-shaped bars, then the Lost strip. */
export const DealsBoardSkeleton = () => <KanbanBoardSkeleton lanes={SALES_STAGES.length} laneMin={LANE_MIN} />
